import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppButton, AppTable, AppSpin, AppTableActionButton } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import type { ColumnsType } from 'antd/es/table';

import type { TreatmentReportListItem } from '@/services/main/treatmentReport';
import {
  useTreatmentReportList,
  TreatmentReportListFilters,
} from '@/hooks/main/useTreatmentReportList';
import { useTreatmentReportRouteParams } from '@/hooks/main/useTreatmentReportRouteParams';
import { useDeleteTreatmentReport } from '@/hooks/main/useDeleteTreatmentReport';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { getAntdTableSortOrder } from '@/utils';
import { getApiErrorMessage } from '@/utils/apiError';
import { convertWarekiMonth } from '@/utils/wareki';
import { formatDateWarekiWrap } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { NotFound } from '@/pages/main/system/NotFound';
import { DELETE_CONFIRM_DATA_MESSAGE } from '@/constants/messages';

/**
 * Format treatment_yearmonth (integer YYYYMM) to display (e.g. "令和08年06月")
 */
function formatYearMonth(ym: number | null | undefined): string {
  if (!ym) return '';
  const s = String(ym);
  if (s.length !== 6) return s;
  const year = parseInt(s.substring(0, 4), 10);
  const month = parseInt(s.substring(4, 6), 10);
  return `${convertWarekiMonth(year, month)}年${String(month).padStart(2, '0')}月`;
}

/**
 * Format date string (YYYY-MM-DD) to display (e.g. "令和08年05月11日")
 */
function formatDate(val: string | null | undefined): string {
  if (!val) return '';
  const parts = val.split('-');
  if (parts.length !== 3) return val;
  const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  return formatDateWarekiWrap(d);
}

const TREATMENT_REPORT_DELETE_ERROR = '削除ができませんでした。';

export const TreatmentReportListPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useParams();
  const toast = useAppToast();
  const { confirm } = useAppModal();

  const [sortField, setSortField] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const derived = useTreatmentReportRouteParams();

  const filters = useMemo<TreatmentReportListFilters>(
    () => ({
      sort_type: sortField,
      sort_dir: sortOrder,
    }),
    [sortField, sortOrder]
  );

  const {
    data: listResponse,
    isLoading,
    isFetching,
    isError,
  } = useTreatmentReportList(derived.patientId, filters);
  const deleteMutation = useDeleteTreatmentReport(derived.patientId);
  const isBusy = isLoading || isFetching;

  const listData = listResponse?.data;
  const patient = listData?.patient;
  const treatmentReports = listData?.treatment_reports ?? [];

  const handleCreate = useCallback(() => {
    navigate(`/${tenant}/treatment-reports/${derived.patientId}/create`);
  }, [navigate, tenant, derived.patientId]);

  const handleCopyCreate = useCallback(() => {
    // Pass the last report (latest by serial_number) as copy source
    const sorted = [...treatmentReports].sort((a, b) => b.serial_number - a.serial_number);
    const lastReport = sorted[0];
    navigate(`/${tenant}/treatment-reports/${derived.patientId}/continue`, {
      state: { copyFrom: lastReport },
    });
  }, [navigate, tenant, derived.patientId, treatmentReports]);

  const handleEdit = useCallback(
    (record: TreatmentReportListItem) => {
      navigate(`/${tenant}/treatment-reports/${derived.patientId}/edit/${record.id}`);
    },
    [navigate, tenant, derived.patientId]
  );

  const handleDeleteConfirm = useCallback(
    async (record: TreatmentReportListItem) => {
      try {
        const response = await deleteMutation.mutateAsync(record.id);
        toast.success(response?.message || '削除が正常に終了しました。');
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err, TREATMENT_REPORT_DELETE_ERROR));
      }
    },
    [deleteMutation, toast]
  );

  const handleDelete = useCallback(
    (record: TreatmentReportListItem) => {
      confirm({
        title: DELETE_CONFIRM_DATA_MESSAGE,
        okButtonProps: { danger: true },
        onOk: async () => {
          await handleDeleteConfirm(record);
        },
        okText: 'OK',
        cancelText: 'キャンセル',
      });
    },
    [confirm, handleDeleteConfirm]
  );

  const handleSortChange = (_pagination: any, _filters: any, sorter: any) => {
    if (isFetching) return;
    const sort = Array.isArray(sorter) ? sorter[0] : sorter;
    const key = sort?.columnKey ? String(sort.columnKey) : sortField;

    if (key === sortField) {
      // Same column: toggle asc ↔ desc
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Different column: start with asc
      setSortField(key);
      setSortOrder('asc');
    }
  };

  const columns = useMemo<ColumnsType<TreatmentReportListItem>>(() => {
    return [
      {
        title: '連番',
        dataIndex: 'serial_number',
        key: 'serial_number',
        align: 'center',
        width: 60,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'serial_number', sortOrder as any),
      },
      {
        title: '施術師',
        dataIndex: 'massager_name',
        key: 'massager_name',
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'massager_name', sortOrder as any),
      },
      {
        title: '施術年月',
        dataIndex: 'treatment_yearmonth',
        key: 'treatment_yearmonth',
        width: 140,
        sorter: true,
        sortOrder: getAntdTableSortOrder(sortField || '', 'treatment_yearmonth', sortOrder as any),
        render: (value: number) => formatYearMonth(value),
      },
      {
        title: '報告書作成日',
        dataIndex: 'treatment_report_date',
        key: 'treatment_report_date',
        width: 180,
        sorter: true,
        sortOrder: getAntdTableSortOrder(
          sortField || '',
          'treatment_report_date',
          sortOrder as any
        ),
        render: (value: string | null) => formatDate(value),
      },
      {
        title: '操作',
        key: 'action',
        width: 170,
        align: 'center' as const,
        render: (_: unknown, record: TreatmentReportListItem) => (
          <div className="flex justify-center gap-1">
            <AppTableActionButton
              action="edit"
              onClick={e => {
                e.stopPropagation();
                handleEdit(record);
              }}
              title="編集"
            >
              編集
            </AppTableActionButton>
            <AppTableActionButton
              action="delete"
              danger
              disabled={isBusy}
              onClick={e => {
                e.stopPropagation();
                handleDelete(record);
              }}
              title="削除"
            >
              削除
            </AppTableActionButton>
          </div>
        ),
      },
    ];
  }, [sortField, sortOrder, isBusy, handleDelete]);

  if (isError && !listData) {
    return <NotFound />;
  }

  return (
    <div className="relative">
      {isFetching && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="flex items-center gap-3 text-gray-700">
            <AppSpin size="large" />
          </div>
        </div>
      )}

      <CollapsibleCard title="施術報告書一覧">
        <PatientPageHeader
          patient={patient}
          patientId={derived.patientId}
          tenant={tenant || ''}
          navigate={navigate}
          activeKey="treatment-reports"
          disabled={isBusy}
          actions={
            <div className="flex gap-8">
              <AppButton
                type="primary"
                disabled={isBusy}
                icon={<i className="fa fa-file-text-o" />}
                onClick={handleCreate}
              >
                新規作成
              </AppButton>
              {treatmentReports.length > 0 && (
                <AppButton
                  type="default"
                  disabled={isBusy}
                  className="!bg-[#17a2b8] !text-white !border-[#17a2b8] hover:!bg-[#138496]"
                  onClick={handleCopyCreate}
                >
                  前回内容をコピーして作成
                </AppButton>
              )}
            </div>
          }
        />

        <div className="relative overflow-x-auto">
          <AppTable<TreatmentReportListItem>
            rowKey={r => String(r.id)}
            columns={columns}
            dataSource={treatmentReports}
            pagination={false}
            bordered
            recordCount={treatmentReports.length}
            onChange={handleSortChange}
            customLabelTotal="表示件数"
          />
        </div>
      </CollapsibleCard>
    </div>
  );
};
